import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import consumerApi from "../lib/consumerApi";


const ConsumerAuthContext = createContext(null);
let cookieSessionTail = Promise.resolve();

// Cookie/CSRF rotations must remain ordered even across provider remounts.
function rotateCookieSession(operation) {
  const pending = cookieSessionTail.then(operation);
  cookieSessionTail = pending.catch(() => undefined);
  return pending;
}

export const consumerAccountsEnabled = () =>
  process.env.REACT_APP_CONSUMER_ACCOUNTS_ENABLED === "true";

export function ConsumerAuthProvider({ children }) {
  const enabled = consumerAccountsEnabled();
  const [account, assignAccount] = useState(null);
  const [csrfToken, assignCsrfToken] = useState("");
  const [ready, setReady] = useState(false);
  const revision = useRef(0);
  const mounted = useRef(true);
  const authenticationOpen = useRef(false);
  const csrf = useRef("");

  const setAccount = useCallback((value) => {
    revision.current += 1;
    assignAccount(value);
    if (!value) { csrf.current = ""; assignCsrfToken(""); }
  }, []);
  const setCsrfToken = useCallback((value) => {
    revision.current += 1;
    csrf.current = value;
    assignCsrfToken(value);
  }, []);
  const isCurrent = useCallback((observed) => mounted.current && revision.current === observed, []);

  const bootstrap = useCallback(async () => {
    if (authenticationOpen.current) return false;
    const observed = revision.current;
    if (!enabled) {
      setAccount(false);
      setReady(true);
      return false;
    }
    try {
      const response = await rotateCookieSession(async () => {
        if (!isCurrent(observed) || authenticationOpen.current) return null;
        return consumerApi.post("/me/session");
      });
      if (!response) return false;
      const { data } = response;
      if (!isCurrent(observed)) return false;
      assignAccount(data.account);
      csrf.current = data.csrf_token;
      assignCsrfToken(data.csrf_token);
      return data.account;
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 404) {
        throw error;
      }
      if (!isCurrent(observed)) return false;
      assignAccount(false);
      csrf.current = "";
      assignCsrfToken("");
      return false;
    } finally {
      if (isCurrent(observed)) setReady(true);
    }
  }, [enabled, isCurrent, setAccount]);

  useEffect(() => {
    mounted.current = true;
    void bootstrap().catch(() => undefined);
    return () => { mounted.current = false; revision.current += 1; };
  }, [bootstrap]);

  const authenticate = useCallback(async (url, payload) => {
    if (!enabled) throw new Error("Consumer accounts are unavailable.");
    if (authenticationOpen.current) throw new Error("An account sign-in or sign-out is already in progress.");
    authenticationOpen.current = true;
    const observed = ++revision.current;
    try {
      return await rotateCookieSession(async () => {
        if (!isCurrent(observed)) return false;
        const { data } = await consumerApi.post(url, payload);
        if (data.verification_required) return false;
        const overview = await consumerApi.get("/me");
        if (!isCurrent(observed)) return false;
        assignAccount(overview.data);
        csrf.current = data.csrf_token;
        assignCsrfToken(data.csrf_token);
        setReady(true);
        return overview.data;
      });
    } finally {
      authenticationOpen.current = false;
      if (isCurrent(observed)) setReady(true);
    }
  }, [enabled, isCurrent]);

  const login = useCallback(async (email, password) => {
    return authenticate("/consumer/auth/login", { email, password });
  }, [authenticate]);

  const googleLogin = useCallback(async (challengeId, idToken, acceptTerms) => {
    return authenticate("/consumer/auth/google/complete", {
      challenge_id: challengeId,
      id_token: idToken,
      terms_accepted: acceptTerms,
      privacy_notice_acknowledged: acceptTerms,
    });
  }, [authenticate]);

  const mutate = useCallback(async (method, url, data) => {
    if (authenticationOpen.current) throw new Error("An account sign-in or sign-out is already in progress.");
    const observed = revision.current;
    return rotateCookieSession(async () => {
      if (!isCurrent(observed) || authenticationOpen.current) throw new Error("Your account session changed. Reload before continuing.");
      const response = await consumerApi.request({
        method,
        url,
        data,
        headers: { "X-CSRF-Token": csrf.current },
      });
      if (!isCurrent(observed)) throw new Error("Your account session changed. Reload before continuing.");
      if (response.data?.csrf_token) {
        csrf.current = response.data.csrf_token;
        assignCsrfToken(response.data.csrf_token);
      }
      return response.data;
    });
  }, [isCurrent]);

  const refresh = useCallback(async () => {
    if (authenticationOpen.current) return false;
    const observed = revision.current;
    const { data } = await consumerApi.get("/me");
    if (!isCurrent(observed)) return false;
    assignAccount(data);
    return data;
  }, [isCurrent]);

  const logout = useCallback(async () => {
    if (authenticationOpen.current) return;
    authenticationOpen.current = true;
    const observed = ++revision.current;
    try {
      await rotateCookieSession(async () => {
        if (!isCurrent(observed)) return;
        // An ignored prior bootstrap may have rotated CSRF on the server.
        const { data } = await consumerApi.post("/me/session");
        if (!isCurrent(observed)) return;
        await consumerApi.request({ method: "post", url: "/consumer/auth/logout",
          headers: { "X-CSRF-Token": data.csrf_token } });
      });
    } finally {
      authenticationOpen.current = false;
      if (isCurrent(observed)) {
        assignAccount(false);
        csrf.current = "";
        assignCsrfToken("");
        setReady(true);
      }
    }
  }, [isCurrent]);

  const value = useMemo(() => ({
    account,
    bootstrap,
    csrfToken,
    enabled,
    googleLogin,
    login,
    logout,
    mutate,
    ready,
    refresh,
    setAccount,
    setCsrfToken,
  }), [account, bootstrap, csrfToken, enabled, googleLogin, login, logout, mutate, ready, refresh, setAccount, setCsrfToken]);

  return (
    <ConsumerAuthContext.Provider value={value}>
      {children}
    </ConsumerAuthContext.Provider>
  );
}

export const useConsumerAuth = () => useContext(ConsumerAuthContext);
